(() => {
  const SECTION_SELECTOR = '.oddit-cart-drawer';

  function formatMoney(cents, currency) {
    const amount = (cents || 0) / 100;
    if (typeof Intl !== 'undefined' && typeof Intl.NumberFormat === 'function') {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency || 'USD',
        }).format(amount);
      } catch (e) {
        // Invalid currency code — fall through to default formatter.
      }
    }
    return '$' + amount.toFixed(2);
  }

  function initSection(section) {
    let currentCurrency = section.dataset.cartCurrency || 'USD';
    const closeBtns = section.querySelectorAll('[data-cart-close]');
    const overlay = section.querySelector('[data-cart-overlay]');
    const panel = section.querySelector('.oddit-cart-drawer__panel');
    const filledState = section.querySelector('[data-cart-filled]');
    const emptyState = section.querySelector('[data-cart-empty]');
    const cartBody = section.querySelector('[data-cart-body]');
    const abortController = new AbortController();
    const signal = abortController.signal;

    // Compare-at prices (in cents) keyed by variant id. cart.js does not expose
    // compare_at_price, so seed the lookup from the server-rendered map and from
    // add-to-cart calls (window.OdditCartDrawer.setCompareAt) to render strikes.
    const compareAtMap = {};
    const compareAtEl = section.querySelector('[data-cart-compare-at-map]');
    if (compareAtEl) {
      try {
        const parsed = JSON.parse(compareAtEl.textContent);
        Object.keys(parsed).forEach((key) => { compareAtMap[key] = parsed[key]; });
      } catch (e) {
        // Malformed map — fall back to no strike-through pricing.
      }
    }

    function setCompareAt(variantId, compareAtCents) {
      if (!variantId) return;
      compareAtMap[String(variantId)] = compareAtCents || 0;
    }

    // ─── UI: Close drawer ───
    function closeDrawer() {
      section.classList.remove('is-open');
      document.body.style.overflow = '';
      if (panel) {
        panel.addEventListener('transitionend', () => {}, { once: true });
      }
    }

    // ─── UI: Open drawer ───
    function openDrawer() {
      section.style.display = '';
      document.body.style.overflow = 'hidden';
      // Force a layout pass with the panel in its initial off-screen
      // state, then add `.is-open` in a subsequent frame so the
      // transform transition runs even on the very first open (when the
      // section was display: none).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          section.classList.add('is-open');
          const firstClose = section.querySelector('[data-cart-close]');
          if (firstClose) firstClose.focus();
        });
      });
    }

    // ─── UI: Toggle drawer ───
    function toggleDrawer() {
      if (section.classList.contains('is-open')) {
        closeDrawer();
      } else {
        openDrawer();
      }
    }

    // Close button clicks (both filled and empty state headers)
    closeBtns.forEach((btn) => {
      btn.addEventListener('click', closeDrawer, { signal });
    });

    // Overlay click closes drawer
    if (overlay) {
      overlay.addEventListener('click', closeDrawer, { signal });
    }

    // Escape key closes drawer
    function handleKeydown(e) {
      if (e.key === 'Escape' && section.classList.contains('is-open')) {
        closeDrawer();
      }
    }
    document.addEventListener('keydown', handleKeydown, { signal });

    // Trap focus inside panel when open
    if (panel) {
      panel.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        const focusableEls = panel.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusableEls.length === 0) return;

        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }, { signal });
    }

    // ─── Cart API Methods ───

    async function getCart() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
      } catch (err) {
        console.error('[OdditCartDrawer] getCart error:', err);
        throw err;
      }
    }

    async function addToCart(variantId, quantity = 1, properties = {}) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity, properties })
        });
        if (!response.ok) throw new Error('Failed to add to cart');
        const cart = await getCart();
        renderCart(cart);
        return cart;
      } catch (err) {
        console.error('[OdditCartDrawer] addToCart error:', err);
        throw err;
      }
    }

    async function addMultipleToCart(items) {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) throw new Error('Failed to add items to cart');
        const cart = await getCart();
        renderCart(cart);
        return cart;
      } catch (err) {
        console.error('[OdditCartDrawer] addMultipleToCart error:', err);
        throw err;
      }
    }

    async function updateLineItemQuantity(lineItemKey, quantity) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItemKey, quantity })
        });
        if (!response.ok) throw new Error('Failed to update line item');
        const cart = await response.json();
        renderCart(cart);
        return cart;
      } catch (err) {
        console.error('[OdditCartDrawer] updateLineItemQuantity error:', err);
        throw err;
      }
    }

    async function removeFromCart(lineItemKey) {
      return updateLineItemQuantity(lineItemKey, 0);
    }

    async function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineItemKey, selling_plan: sellingPlanId })
        });
        if (!response.ok) throw new Error('Failed to update subscription');
        const cart = await response.json();
        renderCart(cart);
        return cart;
      } catch (err) {
        console.error('[OdditCartDrawer] updateLineItemToSubscription error:', err);
        throw err;
      }
    }

    async function refreshCart() {
      try {
        const cart = await getCart();
        renderCart(cart);
        return cart;
      } catch (err) {
        console.error('[OdditCartDrawer] refreshCart error:', err);
      }
    }

    // ─── Progress Bar ───

    function updateProgressBar(cart) {
      const threshold = parseFloat(section.dataset.freeShippingThreshold) || 0;
      if (threshold <= 0) return;

      const cartTotal = cart.total_price / 100;
      const percentage = Math.min((cartTotal / threshold) * 100, 100);
      const remaining = Math.max(threshold - cartTotal, 0);

      const fills = section.querySelectorAll('.oddit-cart-drawer__shipping-fill');
      fills.forEach((fill) => {
        fill.style.width = percentage + '%';
      });

      const texts = section.querySelectorAll('.oddit-cart-drawer__shipping-text');
      texts.forEach((text) => {
        if (remaining <= 0) {
          text.innerHTML = 'You\'ve unlocked <strong>free shipping!</strong>';
        } else {
          text.innerHTML = 'You\'re <strong>' + formatMoney(remaining * 100, currentCurrency) + '</strong> away from free shipping!';
        }
      });
    }

    // ─── Render Line Item HTML ───

    function buildLineItemHTML(item) {
      const variantTitle = item.variant_title ? item.variant_title : '';
      const optionsMeta = item.options_with_values
        ? item.options_with_values.map((opt) =>
            '<div class="oddit-cart-drawer__line-item-meta-row">' +
              '<span class="oddit-cart-drawer__line-item-meta-label">' + escapeHTML(opt.value) + '</span>' +
            '</div>'
          ).join('')
        : (variantTitle
            ? '<div class="oddit-cart-drawer__line-item-meta-row"><span class="oddit-cart-drawer__line-item-meta-label">' + escapeHTML(variantTitle) + '</span></div>'
            : '');

      const metaBlock = optionsMeta
        ? '<div class="oddit-cart-drawer__line-item-meta">' + optionsMeta + '</div>'
        : '';

      const imageURL = item.image ? getSizedImageUrl(item.image, '192x') : '';
      const imageURL2x = item.image ? getSizedImageUrl(item.image, '384x') : '';
      const imageHTML = item.image
        ? '<img class="oddit-cart-drawer__line-item-img" src="' + imageURL + '" srcset="' + imageURL + ' 96w, ' + imageURL2x + ' 192w" sizes="96px" alt="' + escapeHTML(item.title) + '" width="96" height="96" loading="lazy">'
        : '';

      const compareAtUnit = compareAtMap[String(item.variant_id)] || 0;
      const priceHTML = compareAtUnit > item.price
        ? '<s class="oddit-cart-drawer__line-item-price-compare">' + formatMoney(compareAtUnit * item.quantity, currentCurrency) + '</s>' +
          '<span class="oddit-cart-drawer__line-item-price-current">' + formatMoney(item.final_line_price, currentCurrency) + '</span>'
        : formatMoney(item.final_line_price, currentCurrency);

      return (
        '<article class="oddit-cart-drawer__line-item" data-line-item data-line-item-key="' + item.key + '">' +
          '<div class="oddit-cart-drawer__line-item-image">' + imageHTML + '</div>' +
          '<div class="oddit-cart-drawer__line-item-details">' +
            '<div class="oddit-cart-drawer__line-item-info">' +
              '<h3 class="oddit-cart-drawer__line-item-title">' +
                '<a href="' + item.url + '">' + escapeHTML(item.product_title) + '</a>' +
              '</h3>' +
              metaBlock +
              '<p class="oddit-cart-drawer__line-item-price" data-line-item-price>' + priceHTML + '</p>' +
            '</div>' +
            '<div class="oddit-cart-drawer__line-item-actions">' +
              '<div class="oddit-cart-drawer__quantity">' +
                '<button type="button" class="oddit-cart-drawer__quantity-btn" aria-label="Decrease quantity" data-quantity-minus data-line-item-key="' + item.key + '">' +
                  '<svg width="26" height="2" viewBox="0 0 26 1.5" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 0V1.5H8V0H18Z" fill="#6A6A6A"/></svg>' +
                '</button>' +
                '<span class="oddit-cart-drawer__quantity-value" data-quantity-value>' + item.quantity + '</span>' +
                '<button type="button" class="oddit-cart-drawer__quantity-btn" aria-label="Increase quantity" data-quantity-plus data-line-item-key="' + item.key + '">' +
                  '<svg width="26" height="10" viewBox="0 0 26 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 5.7037H13.7037V10H12.2963V5.7037H8V4.2963H12.2963V0H13.7037V4.2963H18V5.7037Z" fill="#1A1A1A"/></svg>' +
                '</button>' +
              '</div>' +
              '<button type="button" class="oddit-cart-drawer__remove-btn" data-remove-item data-line-item-key="' + item.key + '" aria-label="Remove ' + escapeHTML(item.product_title) + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</article>'
      );
    }

    // ─── Render Cart (single DOM orchestrator) ───

    function renderCart(cart) {
      if (cart && cart.currency) currentCurrency = cart.currency;
      // 1. Render line items into the cart body
      if (cartBody) {
        const lineItemsContainer = cartBody;
        const existingLineItems = lineItemsContainer.querySelectorAll('[data-line-item]');
        const existingDividers = lineItemsContainer.querySelectorAll('.oddit-cart-drawer__divider');

        existingLineItems.forEach((el) => el.remove());
        existingDividers.forEach((el) => el.remove());

        if (cart.items.length > 0) {
          const reviewEl = lineItemsContainer.querySelector('.oddit-cart-drawer__review');
          const fragment = document.createDocumentFragment();

          cart.items.forEach((item, index) => {
            const temp = document.createElement('div');
            temp.innerHTML = buildLineItemHTML(item);
            fragment.appendChild(temp.firstElementChild);

            if (index < cart.items.length - 1) {
              const divider = document.createElement('div');
              divider.className = 'oddit-cart-drawer__divider';
              divider.setAttribute('aria-hidden', 'true');
              fragment.appendChild(divider);
            }
          });

          // Add divider before review if review exists
          if (reviewEl) {
            const divider = document.createElement('div');
            divider.className = 'oddit-cart-drawer__divider';
            divider.setAttribute('aria-hidden', 'true');
            fragment.appendChild(divider);
          }

          if (reviewEl) {
            lineItemsContainer.insertBefore(fragment, reviewEl);
          } else {
            lineItemsContainer.appendChild(fragment);
          }
        }
      }

      // 2. Update item count in title(s)
      const titleEls = section.querySelectorAll('[data-cart-title]');
      titleEls.forEach((el) => {
        el.textContent = 'Cart (' + cart.item_count + ')';
      });

      const emptyTitleEls = section.querySelectorAll('[data-cart-title-empty]');
      emptyTitleEls.forEach((el) => {
        el.textContent = 'Cart (0)';
      });

      // 3. Update subtotal
      const subtotalEls = section.querySelectorAll('[data-cart-subtotal]');
      subtotalEls.forEach((el) => {
        el.textContent = formatMoney(cart.total_price, currentCurrency);
      });

      // 4. Update progress bar
      updateProgressBar(cart);

      // 5. Toggle filled/empty states (use the classes the CSS targets)
      if (cart.item_count === 0) {
        if (filledState) {
          filledState.classList.add('is-hidden');
          filledState.style.display = '';
        }
        if (emptyState) {
          emptyState.classList.add('is-active');
          emptyState.style.display = '';
        }
      } else {
        if (filledState) {
          filledState.classList.remove('is-hidden');
          filledState.style.display = '';
        }
        if (emptyState) {
          emptyState.classList.remove('is-active');
          emptyState.style.display = '';
        }
      }

      // 6. Filter cross-sell/upsell cards for products already in cart
      const cartProductIds = cart.items.map((item) => String(item.product_id));
      const crossSellCards = section.querySelectorAll('[data-product-id]');
      crossSellCards.forEach((card) => {
        const productId = card.getAttribute('data-product-id');
        if (cartProductIds.includes(productId)) {
          card.style.display = 'none';
        } else {
          card.style.display = '';
        }
      });

      // Show cross-sell container if there are visible cards and cart has items
      const crossSellContainer = section.querySelector('[data-cross-sell-container]');
      if (crossSellContainer) {
        const visibleCards = crossSellContainer.querySelectorAll('[data-product-id]:not([style*="display: none"])');
        crossSellContainer.style.display = (cart.item_count > 0 && visibleCards.length > 0) ? '' : 'none';
      }

      // 7. Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    // ─── Event Delegation for Quantity, Remove & Cross-sell ───

    section.addEventListener('click', async (e) => {
      const minusBtn = e.target.closest('[data-quantity-minus]');
      const plusBtn = e.target.closest('[data-quantity-plus]');
      const removeBtn = e.target.closest('[data-remove-item]');
      const crossSellBtn = e.target.closest('[data-cross-sell-atc]');

      if (minusBtn) {
        const key = minusBtn.getAttribute('data-line-item-key');
        const valueEl = minusBtn.parentElement.querySelector('[data-quantity-value]');
        const current = valueEl ? parseInt(valueEl.textContent, 10) || 1 : 1;
        if (current <= 1) {
          await removeFromCart(key);
        } else {
          await updateLineItemQuantity(key, current - 1);
        }
        return;
      }

      if (plusBtn) {
        const key = plusBtn.getAttribute('data-line-item-key');
        const valueEl = plusBtn.parentElement.querySelector('[data-quantity-value]');
        const current = valueEl ? parseInt(valueEl.textContent, 10) || 1 : 1;
        await updateLineItemQuantity(key, current + 1);
        return;
      }

      if (removeBtn) {
        const key = removeBtn.getAttribute('data-line-item-key');
        await removeFromCart(key);
        return;
      }

      if (crossSellBtn) {
        const variantId = Number(crossSellBtn.getAttribute('data-variant-id'));
        if (!variantId) return;
        crossSellBtn.disabled = true;
        try {
          await addToCart(variantId);
        } finally {
          crossSellBtn.disabled = false;
        }
        return;
      }
    }, { signal });

    // ─── Wire Upsell Buttons ───

    const recAddBtns = section.querySelectorAll('.oddit-cart-drawer__rec-add[data-variant-id]');
    recAddBtns.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = Number(btn.getAttribute('data-variant-id'));
        btn.disabled = true;
        try {
          await addToCart(variantId);
        } finally {
          btn.disabled = false;
        }
      }, { signal });
    });

    const upsellCtaBtns = section.querySelectorAll('.oddit-cart-drawer__upsell-cta[data-variant-id]');
    upsellCtaBtns.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = Number(btn.getAttribute('data-variant-id'));
        btn.disabled = true;
        try {
          await addToCart(variantId);
        } finally {
          btn.disabled = false;
        }
      }, { signal });
    });

    const switchCtaBtns = section.querySelectorAll('.oddit-cart-drawer__switch-cta[data-selling-plan-id]');
    switchCtaBtns.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const sellingPlanId = Number(btn.getAttribute('data-selling-plan-id'));
        btn.disabled = true;
        try {
          const cart = await getCart();
          for (const item of cart.items) {
            await updateLineItemToSubscription(item.key, sellingPlanId);
          }
        } finally {
          btn.disabled = false;
        }
      }, { signal });
    });

    // ─── Initial Render ───

    refreshCart();

    // ─── Public API ───

    window.OdditCartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      toggle: toggleDrawer,
      addToCart,
      addMultipleToCart,
      removeFromCart,
      updateLineItemQuantity,
      updateLineItemToSubscription,
      getCart,
      refreshCart,
      setCompareAt
    };

    // Store cleanup reference
    section._odditCleanup = {
      abort: () => abortController.abort()
    };
  }

  // ─── Helpers ───

  function escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    if (url.indexOf('_' + size + '.') !== -1) return url;
    const match = url.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp|avif)(\?v=\d+)?$/i);
    if (match) {
      const prefix = url.substring(0, match.index);
      const suffix = url.substring(match.index);
      return prefix + '_' + size + suffix;
    }
    return url;
  }

  function destroySection(section) {
    if (section._odditCleanup) {
      if (section._odditCleanup.abort) {
        section._odditCleanup.abort();
      }
      section._odditCleanup = null;
    }
    if (window.OdditCartDrawer) {
      delete window.OdditCartDrawer;
    }
  }

  // Initialize all instances
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();